import type {
  ActiveBossEffect,
  BossEffectKind,
  BossState,
  CellCoord,
  GameDelta,
  GameEvent,
  GameState,
  PingKind,
  PlayerTributeSummary,
  ServerToClientMessage,
  TargetingMode,
  Tower,
  TowerType,
} from "@mmotdg/shared";
import { TOWER_DEFINITIONS } from "@mmotdg/shared";
import { gameSocket } from "../network/WebSocketClient.js";
import { GameRenderer } from "./GameRenderer.js";
import { getEquippedSkinId } from "./models/towers/index.js";

export interface MatchControllerCallbacks {
  onStateChange: (state: GameState) => void;
  onEvent: (event: GameEvent) => void;
  onGameOver: (winnerId: string | null) => void;
  onChat: (from: string, text: string) => void;
  onError: (code: string) => void;
  onDamage?: (worldX: number, worldY: number, amount: number, isKill: boolean) => void;
  onTowerFired?: (towerId: string) => void;
  onShake?: (intensity: number, durationMs: number) => void;
  onBossState?: (state: BossState) => void;
  onBossEffectApplied?: (effect: ActiveBossEffect) => void;
  onBossEffectExpired?: (kind: BossEffectKind) => void;
  onTributeUpdate?: (update: {
    playerId: string;
    delta: number;
    total: number;
    wave: number;
    tributes: PlayerTributeSummary[];
  }) => void;
  onStakeTransfer?: (info: {
    fromUserId: string;
    splits: Array<{ toUserId: string; amount: number }>;
    reason: "inherit" | "win" | "house";
  }) => void;
}

const DRAG_THRESHOLD_PX_SQ = 25;

export class MatchController {
  private state: GameState | null = null;
  private renderer: GameRenderer;
  private selectedTowerType: TowerType | null = null;
  private selectedTower: Tower | null = null;
  private hoverCell: CellCoord | null = null;
  private callbacks: MatchControllerCallbacks;
  private unsubscribe: (() => void) | null = null;
  private currentUserId: string;
  private pointerStart: { x: number; y: number } | null = null;
  private pointerWasDrag = false;
  private prevMobHp = new Map<string, { hp: number; x: number; y: number }>();

  constructor(canvas: HTMLCanvasElement, userId: string, callbacks: MatchControllerCallbacks) {
    this.renderer = new GameRenderer(canvas, userId);
    this.callbacks = callbacks;
    this.currentUserId = userId;
    this.setupSocketListener();
    this.setupInput(canvas);
  }

  private setupSocketListener(): void {
    this.unsubscribe = gameSocket.onMessage((msg) => this.handleMessage(msg));
  }

  private handleMessage(msg: ServerToClientMessage): void {
    switch (msg.type) {
      case "game_snapshot":
        this.state = msg.state;
        this.renderer.initialize(msg.state);
        this.renderer.start();
        this.refreshPrevMobHp();
        this.callbacks.onStateChange(msg.state);
        break;
      case "game_delta":
        if (!this.state) return;
        this.applyDelta(msg.delta);
        this.callbacks.onStateChange(this.state);
        break;
      case "game_event":
        this.handleGameEvent(msg.event);
        this.callbacks.onEvent(msg.event);
        break;
      case "economy_update":
        if (!this.state) return;
        const p = this.state.players.find((pl) => pl.id === msg.playerId);
        if (p) {
          p.gold = msg.gold;
          p.income = msg.income;
          this.callbacks.onStateChange(this.state);
        }
        break;
      case "game_over":
        this.callbacks.onGameOver(msg.winnerId);
        break;
      case "chat":
        this.callbacks.onChat(msg.from, msg.text);
        break;
      case "error":
        this.callbacks.onError(msg.code);
        break;
      case "boss_state":
        if (this.state) this.state.bossState = msg.state;
        this.renderer.applyBossState(msg.state);
        this.callbacks.onBossState?.(msg.state);
        break;
      case "boss_effect_applied":
        this.renderer.applyBossEffect(msg.effect.kind);
        this.callbacks.onBossEffectApplied?.(msg.effect);
        break;
      case "boss_effect_expired":
        this.renderer.applyBossEffect(null);
        this.callbacks.onBossEffectExpired?.(msg.kind);
        break;
      case "tribute_update":
        this.callbacks.onTributeUpdate?.({
          playerId: msg.playerId,
          delta: msg.delta,
          total: msg.total,
          wave: msg.wave,
          tributes: msg.tributes,
        });
        break;
      case "stake_transfer":
        this.callbacks.onStakeTransfer?.({
          fromUserId: msg.fromUserId,
          splits: msg.splits,
          reason: msg.reason,
        });
        break;
    }
  }

  private handleGameEvent(event: GameEvent): void {
    if (!this.state) return;
    if (event.kind === "wave_start") this.state.currentWave = event.wave;
    if (event.kind === "mob_killed") {
      const known = this.prevMobHp.get(event.mobId);
      if (known && this.callbacks.onDamage) {
        this.callbacks.onDamage(known.x, known.y, known.hp, true);
      }
    }
  }

  private refreshPrevMobHp(): void {
    this.prevMobHp.clear();
    if (!this.state) return;
    for (const m of this.state.mobs) {
      this.prevMobHp.set(m.id, { hp: m.hp, x: m.position.x, y: m.position.y });
    }
  }

  private applyDelta(delta: GameDelta): void {
    if (!this.state) return;
    const state = this.state;

    if (delta.mobsAdded.length > 0) {
      for (const m of delta.mobsAdded) {
        state.mobs.push(m);
        this.prevMobHp.set(m.id, { hp: m.hp, x: m.position.x, y: m.position.y });
        this.renderer.pulseMobSpawn(m.position.x, m.position.y, m.stats.isFlying);
      }
    }
    if (delta.mobsRemoved.length > 0) {
      const removed = new Set(delta.mobsRemoved);
      state.mobs = state.mobs.filter((m) => !removed.has(m.id));
      for (const id of removed) this.prevMobHp.delete(id);
    }

    for (const u of delta.mobsUpdated) {
      const mob = state.mobs.find((m) => m.id === u.id);
      if (mob) {
        const prev = this.prevMobHp.get(u.id);
        if (prev && u.hp < prev.hp) {
          if (this.callbacks.onDamage) {
            const dmg = prev.hp - u.hp;
            this.callbacks.onDamage(u.x, u.y, dmg, false);
          }
          this.renderer.flashMobDamage(u.id);
        }
        mob.position.x = u.x;
        mob.position.y = u.y;
        mob.hp = u.hp;
        this.prevMobHp.set(u.id, { hp: u.hp, x: u.x, y: u.y });
      }
    }

    if (delta.towersAdded.length > 0) {
      for (const t of delta.towersAdded) state.towers.push(t);
    }
    if (delta.towersRemoved.length > 0) {
      const removed = new Set(delta.towersRemoved);
      state.towers = state.towers.filter((t) => !removed.has(t.id));
    }
    for (const u of delta.towersUpdated) {
      const tower = state.towers.find((t) => t.id === u.id);
      if (tower) {
        tower.level = u.level;
        tower.stats = u.stats;
        tower.totalInvested = u.totalInvested;
      }
    }

    if (delta.projectilesAdded.length > 0) {
      for (const p of delta.projectilesAdded) {
        state.projectiles.push(p);
        if (this.callbacks.onTowerFired) this.callbacks.onTowerFired(p.towerId);
      }
    }
    if (delta.projectilesRemoved.length > 0) {
      const removed = new Set(delta.projectilesRemoved);
      const removedProjs = state.projectiles.filter((p) => removed.has(p.id));
      state.projectiles = state.projectiles.filter((p) => !removed.has(p.id));
      for (const p of removedProjs) {
        this.renderer.spawnHitSpark(p.position.x, p.position.y, p.splashRadius);
      }
    }
    for (const u of delta.projectilesUpdated) {
      const proj = state.projectiles.find((p) => p.id === u.id);
      if (proj) {
        proj.position.x = u.x;
        proj.position.y = u.y;
      }
    }

    for (const lu of delta.lanesUpdated) {
      const lane = state.lanes.find((l) => l.id === lu.id);
      if (!lane) continue;
      const hpDelta = lane.baseHp - lu.baseHp;
      lane.baseHp = lu.baseHp;
      if (lu.path) lane.currentPath = lu.path;
      if (hpDelta > 0) this.renderer.pulseBase(lane.baseCell.cx + 0.5, lane.baseCell.cy + 0.5);
    }

    for (const pu of delta.playersUpdated) {
      const player = state.players.find((p) => p.id === pu.id);
      if (player) {
        player.gold = pu.gold;
        player.income = pu.income;
        player.score = pu.score;
        player.isAlive = pu.isAlive;
      }
    }

    state.currentWave = delta.currentWave;
    state.waveInProgress = delta.waveInProgress;

    this.renderer.applyDelta(delta, state);
  }

  private setupInput(canvas: HTMLCanvasElement): void {
    canvas.addEventListener("pointerdown", (e) => {
      this.pointerStart = { x: e.clientX, y: e.clientY };
      this.pointerWasDrag = false;
    });

    canvas.addEventListener("pointermove", (e) => {
      if (this.pointerStart && !this.pointerWasDrag) {
        const dx = e.clientX - this.pointerStart.x;
        const dy = e.clientY - this.pointerStart.y;
        if (dx * dx + dy * dy > DRAG_THRESHOLD_PX_SQ) this.pointerWasDrag = true;
      }
    });

    canvas.addEventListener("mousemove", (e) => {
      const ndc = this.toNDC(e, canvas);
      const cell = this.renderer.pickGridCell(ndc);
      this.hoverCell = cell;
      if (cell && this.selectedTowerType) {
        const valid = this.canPlaceAtCell(cell);
        this.renderer.highlightCell(cell, valid);
        const range = this.getSelectedTowerTypeRange();
        if (range !== null) {
          this.renderer.showRangePreview({ x: cell.cx + 0.5, y: cell.cy + 0.5, range });
        }
      } else if (!this.selectedTower) {
        this.renderer.highlightCell(null, true);
        this.renderer.showRangePreview(null);
      }
    });

    canvas.addEventListener("click", (e) => {
      const wasDrag = this.pointerWasDrag;
      this.pointerStart = null;
      this.pointerWasDrag = false;
      if (wasDrag) return;

      const ndc = this.toNDC(e, canvas);
      const cell = this.renderer.pickGridCell(ndc);
      if (!cell) return;

      const tower = this.findTowerAtCell(cell);
      if (tower) {
        this.selectedTower = tower;
        this.renderer.setSelectedTower(tower.id);
        if (this.selectedTowerType) {
          this.selectedTowerType = null;
          this.renderer.highlightCell(null, true);
        }
        if (this.state) this.callbacks.onStateChange(this.state);
        return;
      }

      if (this.selectedTowerType) {
        if (this.canPlaceAtCell(cell)) {
          const skinId = getEquippedSkinId(this.selectedTowerType);
          gameSocket.send({
            type: "place_tower",
            cell,
            towerType: this.selectedTowerType,
            ...(skinId !== "default" ? { skinId } : {}),
          });
        }
        return;
      }

      if (this.selectedTower) {
        this.selectedTower = null;
        this.renderer.setSelectedTower(null);
        if (this.state) this.callbacks.onStateChange(this.state);
      }
    });

    canvas.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      const wasDrag = this.pointerWasDrag;
      this.pointerStart = null;
      this.pointerWasDrag = false;
      if (wasDrag) return;
      const had = this.selectedTowerType !== null || this.selectedTower !== null;
      this.selectedTowerType = null;
      this.selectedTower = null;
      this.renderer.highlightCell(null, true);
      this.renderer.setSelectedTower(null);
      this.renderer.showRangePreview(null);
      if (had && this.state) this.callbacks.onStateChange(this.state);
    });
  }

  private toNDC(e: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((e.clientY - rect.top) / rect.height) * 2 + 1,
    };
  }

  private canPlaceAtCell(cell: CellCoord): boolean {
    if (!this.state) return false;
    if (cell.cx < 0 || cell.cy < 0 || cell.cx >= this.state.gridWidth || cell.cy >= this.state.gridHeight) return false;
    if (this.state.obstacles.some((o) => o.cx === cell.cx && o.cy === cell.cy)) return false;
    if (this.state.towers.some((t) => t.cell.cx === cell.cx && t.cell.cy === cell.cy)) return false;
    if (this.state.lanes.some((l) =>
      (l.spawnCell.cx === cell.cx && l.spawnCell.cy === cell.cy) ||
      (l.baseCell.cx === cell.cx && l.baseCell.cy === cell.cy),
    )) return false;
    return true;
  }

  private findTowerAtCell(cell: CellCoord): Tower | null {
    if (!this.state) return null;
    return this.state.towers.find((t) => t.cell.cx === cell.cx && t.cell.cy === cell.cy) ?? null;
  }

  private getSelectedTowerTypeRange(): number | null {
    if (!this.selectedTowerType) return null;
    const def = TOWER_DEFINITIONS[this.selectedTowerType];
    return def?.baseStats?.range ?? null;
  }

  getState(): GameState | null {
    return this.state;
  }

  getMe() {
    return this.state?.players.find((p) => p.id === this.currentUserId) ?? null;
  }

  selectTowerType(type: TowerType | null): void {
    this.selectedTowerType = type;
    if (!type) {
      this.renderer.highlightCell(null, true);
      this.renderer.showRangePreview(null);
    }
  }

  getSelectedTowerType(): TowerType | null {
    return this.selectedTowerType;
  }

  getSelectedTower(): Tower | null {
    return this.selectedTower;
  }

  clearTowerSelection(): void {
    this.selectedTower = null;
    this.renderer.setSelectedTower(null);
    this.renderer.showRangePreview(null);
  }

  showRangePreview(target: { x: number; y: number; range: number } | null): void {
    this.renderer.showRangePreview(target);
  }

  renderTowerFireFlash(towerId: string): void {
    this.renderer.flashTower(towerId);
  }

  shake(intensity: number, durationMs: number): void {
    this.renderer.shake(intensity, durationMs);
    this.callbacks.onShake?.(intensity, durationMs);
  }

  worldToScreen(worldX: number, worldY: number): { x: number; y: number } | null {
    return this.renderer.worldToScreen(worldX, worldY);
  }

  projectFlatToViewport(flatX: number, flatY: number) {
    return this.renderer.projectFlatToViewport(flatX, flatY);
  }

  upgradeSelected(): void {
    if (this.selectedTower) gameSocket.send({ type: "upgrade_tower", towerId: this.selectedTower.id });
  }

  sellSelected(): void {
    if (this.selectedTower) gameSocket.send({ type: "sell_tower", towerId: this.selectedTower.id });
    this.selectedTower = null;
    this.renderer.setSelectedTower(null);
    this.renderer.showRangePreview(null);
    if (this.state) this.callbacks.onStateChange(this.state);
  }

  setTargetingMode(mode: TargetingMode): void {
    if (!this.selectedTower) return;
    this.selectedTower.targetingMode = mode;
    gameSocket.send({ type: "set_targeting", towerId: this.selectedTower.id, mode });
  }

  // Spectator helpers — used when the active player is dead and cycles
  // through other players' lanes with arrow keys.
  cycleSpectator(direction: 1 | -1): string | null {
    if (!this.state) return null;
    return this.renderer.cycleSpectator(direction, this.state);
  }

  resetSpectator(): void {
    this.renderer.spectateLane(null);
  }

  spectatedLane(): string | null {
    return this.renderer.spectatedLaneId;
  }

  sendReady(): void {
    gameSocket.send({ type: "ready" });
  }

  sendPing(cell: CellCoord, kind: PingKind): void {
    gameSocket.send({ type: "ping_in_game", cell, kind });
  }

  sendMob(mobType: any): void {
    gameSocket.send({ type: "send_mob", mobType });
  }

  destroy(): void {
    this.unsubscribe?.();
    this.renderer.dispose();
    this.prevMobHp.clear();
  }
}
